import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Space, Typography, Card, Row, Col, Select, Upload } from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined, 
  SearchOutlined, 
  FileTextOutlined, 
  TagsOutlined, 
  LinkOutlined,
  GlobalOutlined,
  ShareAltOutlined,
  TwitterOutlined,
  FacebookOutlined,
  UploadOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import masterService from '../../services/masterService';
import { useNotify } from '../../context/NotificationContext';
import { getImageUrl } from '../../utils/commonFunctions';

const { TextArea } = Input;
const { Text } = Typography;

const CollectionSeoForm = ({ initialValues, loading, onSubmit, onBack, languages = [] }) => {
  const [form] = Form.useForm();
  const notify = useNotify();
  const [uploading, setUploading] = useState({});

  // Convert initial keywords string to array for tags input
  useEffect(() => {
    // Normalize API shape where SEO lives under c_collection_seo_meta
    if (initialValues?.c_collection_seo_meta) {
      const meta = initialValues.c_collection_seo_meta;
      const toArray = (v) => typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(v) ? v : []);

      const base = {
        seotitle: meta.title || '',
        seodescription: meta.description || '',
        seokeyword: toArray(meta.keywords),
        slug: meta.slug || '',
        canonical: meta.canonical || '',
        og_title: meta.og_title || '',
        og_description: meta.og_description || '',
        og_image: meta.og_image || '',
        og_url: meta.og_url || '',
        og_keywords: toArray(meta.og_keywords),
        twitter_title: meta.twitter_title || '',
        twitter_description: meta.twitter_description || '',
        twitter_image: meta.twitter_image || '',
        twitter_keywords: toArray(meta.twitter_keywords),
        facebook_title: meta.facebook_title || '',
        facebook_description: meta.facebook_description || '',
        facebook_image: meta.facebook_image || '',
        facebook_keywords: toArray(meta.facebook_keywords),
        image: meta.image || '',
      };

      // Prefill multilingual entries if present
      const ml = Array.isArray(meta.multilingual_seo) ? meta.multilingual_seo.map((m) => ({
        lang_id: m.fk_lang_id ?? m.lang_id ?? null,
        seotitle: m.title || '',
        seodescription: m.description || '',
        seokeyword: toArray(m.keywords),
        slug: m.slug || '',
        canonical: m.canonical || '',
        og_title: m.og_title || '',
        og_description: m.og_description || '',
        og_image: m.og_image || '',
        og_url: m.og_url || '',
        og_keywords: toArray(m.og_keywords),
        twitter_title: m.twitter_title || '',
        twitter_description: m.twitter_description || '',
        twitter_image: m.twitter_image || '',
        twitter_keywords: toArray(m.twitter_keywords),
        facebook_title: m.facebook_title || '',
        facebook_description: m.facebook_description || '',
        facebook_image: m.facebook_image || '',
        facebook_keywords: toArray(m.facebook_keywords),
      })) : [];

      form.setFieldsValue({ ...base, multilingual_seo: ml });
    }

    if (initialValues?.seokeyword) {
      const keywordsArray = typeof initialValues.seokeyword === 'string' 
        ? initialValues.seokeyword.split(',').map(k => k.trim()).filter(k => k.length > 0)
        : initialValues.seokeyword;
      form.setFieldsValue({ seokeyword: keywordsArray });
    }
    if (initialValues?.og_keywords) {
      const ogKeywordsArray = typeof initialValues.og_keywords === 'string'
        ? initialValues.og_keywords.split(',').map(k => k.trim()).filter(Boolean)
        : initialValues.og_keywords;
      form.setFieldsValue({ og_keywords: ogKeywordsArray });
    }
    if (initialValues?.twitter_keywords) {
      const twKeywordsArray = typeof initialValues.twitter_keywords === 'string'
        ? initialValues.twitter_keywords.split(',').map(k => k.trim()).filter(Boolean)
        : initialValues.twitter_keywords;
      form.setFieldsValue({ twitter_keywords: twKeywordsArray });
    }
    if (Array.isArray(initialValues?.multilingual_seo)) {
      const parsed = initialValues.multilingual_seo.map(entry => ({
        ...entry,
        seokeyword: typeof entry.seokeyword === 'string' ? entry.seokeyword.split(',').map(s => s.trim()).filter(Boolean) : (entry.seokeyword || []),
        og_keywords: typeof entry.og_keywords === 'string' ? entry.og_keywords.split(',').map(s => s.trim()).filter(Boolean) : (entry.og_keywords || []),
        twitter_keywords: typeof entry.twitter_keywords === 'string' ? entry.twitter_keywords.split(',').map(s => s.trim()).filter(Boolean) : (entry.twitter_keywords || []),
        facebook_keywords: typeof entry.facebook_keywords === 'string' ? entry.facebook_keywords.split(',').map(s => s.trim()).filter(Boolean) : (entry.facebook_keywords || []),
      }));
      form.setFieldsValue({ multilingual_seo: parsed });
    }
  }, [initialValues, form]);

  // Handle form submission with keywords conversion
  const handleSubmit = (values) => {
    // Convert keywords array to comma-separated string for backend
    const processedValues = {
      ...values,
      seokeyword: Array.isArray(values.seokeyword) 
        ? values.seokeyword.join(',') 
        : values.seokeyword || null,
      og_keywords: Array.isArray(values.og_keywords)
        ? values.og_keywords.join(',')
        : values.og_keywords || null,
      twitter_keywords: Array.isArray(values.twitter_keywords)
        ? values.twitter_keywords.join(',')
        : values.twitter_keywords || null,
      facebook_keywords: Array.isArray(values.facebook_keywords)
        ? values.facebook_keywords.join(',')
        : values.facebook_keywords || null,
    };
    if (Array.isArray(values.multilingual_seo)) {
      processedValues.multilingual_seo = values.multilingual_seo.map(entry => ({
        ...entry,
        seokeyword: Array.isArray(entry.seokeyword) ? entry.seokeyword.join(',') : (entry.seokeyword || null),
        og_keywords: Array.isArray(entry.og_keywords) ? entry.og_keywords.join(',') : (entry.og_keywords || null),
        twitter_keywords: Array.isArray(entry.twitter_keywords) ? entry.twitter_keywords.join(',') : (entry.twitter_keywords || null),
        facebook_keywords: Array.isArray(entry.facebook_keywords) ? entry.facebook_keywords.join(',') : (entry.facebook_keywords || null),
      }));
    }
    
    onSubmit && onSubmit(processedValues);
  };

  // Image upload handler
  const handleImageUpload = async (file, fieldName) => {
    try {
      setUploading(prev => ({ ...prev, [fieldName]: true }));
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "collections");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const s3Key = response.data?.data?.s3_key;
      if (s3Key) {
        form.setFieldsValue({ [fieldName]: s3Key });
        notify.success({ message: "Image uploaded successfully" });
      }
    } catch (error) {
      notify.error({ message: "Failed to upload image" });
    } finally {
      setUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Image upload component
  const ImageUploadField = ({ fieldName, label, aspect = 16/9, tooltip }) => {
    return (
      <>
        <Form.Item 
          name={fieldName} 
          noStyle
        >
          <Input type="hidden" />
        </Form.Item>
        <Form.Item
          label={label}
          tooltip={tooltip ? { title: tooltip, icon: <InfoCircleOutlined /> } : undefined}
          shouldUpdate={(prevValues, currentValues) => prevValues[fieldName] !== currentValues[fieldName]}
        >
          {({ getFieldValue }) => {
            const imageValue = getFieldValue(fieldName);
            return (
              <>
                {imageValue && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{
                      padding: "8px 12px",
                      background: "#fff7e6",
                      border: "1px solid #ffd591",
                      borderRadius: 6,
                      color: "#fa8c16",
                      marginBottom: 8,
                    }}>
                      <Text strong>{getImageUrl(imageValue)}</Text>
                    </div>
                    <div style={{
                      width: "100%",
                      height: "120px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 6,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#fafafa",
                    }}>
                      <img
                        src={getImageUrl(imageValue)}
                        alt={label}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </div>
                )}
                <ImgCrop aspect={aspect} rotationSlider quality={1}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleImageUpload(file, fieldName);
                      return false;
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={uploading[fieldName]}
                      style={{ width: "100%" }}
                    >
                      {imageValue ? "Change Image" : "Upload Image"}
                    </Button>
                  </Upload>
                </ImgCrop>
              </>
            );
          }}
        </Form.Item>
      </>
    );
  };

  return (
    <Form layout="vertical" form={form} initialValues={initialValues} onFinish={handleSubmit} style={{ maxWidth: 1100 }}>
      
      {/* Basic SEO Meta Section */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }} 
        title={
          <Space>
            <SearchOutlined style={{ color: '#1890ff' }} />
            <span>Search Engine Optimization</span>
          </Space>
        }
        bodyStyle={{ paddingBottom: 8 }}
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="seotitle" 
              label="SEO Title"
              tooltip={{ title: 'Title that appears in search engine results (50-60 characters recommended)', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<FileTextOutlined />}
                placeholder="Enter SEO title..."
                showCount
                maxLength={60}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="slug" 
              label="SEO Slug"
              tooltip={{ title: 'URL-friendly version of the title for SEO purposes', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<LinkOutlined />}
                placeholder="seo-friendly-slug"
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item 
              name="seodescription" 
              label="SEO Description"
              tooltip={{ title: 'Description that appears in search results (150-160 characters recommended)', icon: <InfoCircleOutlined /> }}
            >
              <TextArea 
                rows={3} 
                placeholder="Enter SEO description..."
                showCount
                maxLength={160}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="seokeyword" 
              label="SEO Keywords"
              tooltip={{ title: 'Add keywords relevant to your content. Press Enter or comma to add each keyword as a tag.', icon: <InfoCircleOutlined /> }}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Type keywords and press Enter..."
                tokenSeparators={[',']}
                suffixIcon={<TagsOutlined />}
                maxTagCount="responsive"
                options={[]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="canonical" 
              label="Canonical URL"
              tooltip={{ title: 'The preferred URL for this content to avoid duplicate content issues', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<GlobalOutlined />}
                placeholder="https://example.com/canonical-url"
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <ImageUploadField 
              fieldName="image" 
              label="SEO Image"
              tooltip="Main image for SEO purposes (1200x630px recommended)"
            />
          </Col>
        </Row>
      </Card>

      {/* Open Graph Section */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }} 
        title={
          <Space>
            <ShareAltOutlined style={{ color: '#4267B2' }} />
            <span>Open Graph (Facebook & Social)</span>
          </Space>
        }
        bodyStyle={{ paddingBottom: 8 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Configure how your content appears when shared on Facebook, LinkedIn, and other social platforms
        </Text>
        <Row gutter={[16, 8]}>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="og_title" 
              label="Open Graph Title"
              tooltip={{ title: 'Title when shared on social media', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<FileTextOutlined />}
                placeholder="Social media title..."
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="og_url" 
              label="Open Graph URL"
              tooltip={{ title: 'The URL that will be used when content is shared', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<LinkOutlined />}
                placeholder="https://example.com/page-url"
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="og_keywords" 
              label="Open Graph Keywords"
              tooltip={{ title: 'Add keywords for Open Graph. Press Enter or comma to add each keyword as a tag.', icon: <InfoCircleOutlined /> }}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Type OG keywords and press Enter..."
                tokenSeparators={[',']}
                suffixIcon={<TagsOutlined />}
                maxTagCount="responsive"
                options={[]}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item 
              name="og_description" 
              label="Open Graph Description"
              tooltip={{ title: 'Description when shared on social media', icon: <InfoCircleOutlined /> }}
            >
              <TextArea 
                rows={3} 
                placeholder="Description for social media sharing..."
                showCount
                maxLength={200}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <ImageUploadField 
              fieldName="og_image" 
              label="Open Graph Image"
              tooltip="Image that appears when shared (1200x630px recommended)"
              aspect={1200/630}
            />
          </Col>
        </Row>
      </Card>

      {/* Twitter Section */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }} 
        title={
          <Space>
            <TwitterOutlined style={{ color: '#1DA1F2' }} />
            <span>Twitter Cards</span>
          </Space>
        }
        bodyStyle={{ paddingBottom: 8 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Optimize how your content appears when shared on Twitter
        </Text>
        <Row gutter={[16, 8]}>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="twitter_title" 
              label="Twitter Title"
              tooltip={{ title: 'Title for Twitter card', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<FileTextOutlined />}
                placeholder="Twitter card title..."
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <ImageUploadField 
              fieldName="twitter_image" 
              label="Twitter Image"
              tooltip="Image for Twitter card (1200x600px recommended)"
              aspect={1200/600}
            />
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="twitter_keywords" 
              label="Twitter Keywords"
              tooltip={{ title: 'Add keywords for Twitter card. Press Enter or comma to add each keyword as a tag.', icon: <InfoCircleOutlined /> }}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Type Twitter keywords and press Enter..."
                tokenSeparators={[',']}
                suffixIcon={<TagsOutlined />}
                maxTagCount="responsive"
                options={[]}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item 
              name="twitter_description" 
              label="Twitter Description"
              tooltip={{ title: 'Description for Twitter card', icon: <InfoCircleOutlined /> }}
            >
              <TextArea 
                rows={3} 
                placeholder="Description for Twitter sharing..."
                showCount
                maxLength={200}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Facebook Section */}
      <Card 
        size="small" 
        style={{ marginBottom: 16 }} 
        title={
          <Space>
            <FacebookOutlined style={{ color: '#4267B2' }} />
            <span>Facebook Cards</span>
          </Space>
        }
        bodyStyle={{ paddingBottom: 8 }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Optimize how your content appears when shared on Facebook
        </Text>
        <Row gutter={[16, 8]}>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="facebook_title" 
              label="Facebook Title"
              tooltip={{ title: 'Title for Facebook card', icon: <InfoCircleOutlined /> }}
            >
              <Input 
                prefix={<FileTextOutlined />}
                placeholder="Facebook card title..."
              />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <ImageUploadField 
              fieldName="facebook_image" 
              label="Facebook Image"
              tooltip="Image for Facebook card (1200x630px recommended)"
              aspect={1200/630}
            />
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item 
              name="facebook_keywords" 
              label="Facebook Keywords"
              tooltip={{ title: 'Add keywords for Facebook card. Press Enter or comma to add each keyword as a tag.', icon: <InfoCircleOutlined /> }}
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Type Facebook keywords and press Enter..."
                tokenSeparators={[',']}
                suffixIcon={<TagsOutlined />}
                maxTagCount="responsive"
                options={[]}
              />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item 
              name="facebook_description" 
              label="Facebook Description"
              tooltip={{ title: 'Description for Facebook card', icon: <InfoCircleOutlined /> }}
            >
              <TextArea 
                rows={3} 
                placeholder="Description for Facebook sharing..."
                showCount
                maxLength={200}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Multilingual SEO Section */}
      <Card size="small" style={{ marginBottom: 16 }} title={
        <Space>
          <GlobalOutlined />
          <span>Multilingual SEO</span>
        </Space>
      }>
        <Form.List name="multilingual_seo">
          {(fields, { add, remove }) => (
            <>
              {fields.length === 0 && (
                <Card size="small" style={{ textAlign: 'center', background: '#fafafa', marginBottom: 12 }}>
                  <Text type="secondary">No multilingual SEO added yet. Click "Add Language" to get started.</Text>
                </Card>
              )}
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} size="small" style={{ marginBottom: 12 }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong>Language Entry #{name + 1}</Text>
                      <Button danger type="link" onClick={() => remove(name)}>Remove</Button>
                    </div>
                  }
                >
                  <Row gutter={[16,8]}>
                    <Col xs={24} lg={12}>
                      <Form.Item {...restField} name={[name, 'lang_id']} label="Language">
                        {Array.isArray(languages) && languages.length > 0 ? (
                          <Select placeholder="Select language" showSearch optionFilterProp="children">
                            {languages.map(l => (
                              <Select.Option key={l.lang_id} value={l.lang_id}>{l.lang_name}</Select.Option>
                            ))}
                          </Select>
                        ) : (
                          <Input placeholder="Language ID or Code" />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Basic SEO Meta Section */}
                  <Card size="small" style={{ marginBottom: 12 }} title={(<Space><SearchOutlined style={{ color: '#1890ff' }} /><span>Search Engine Optimization</span></Space>)} bodyStyle={{ paddingBottom: 8 }}>
                    <Row gutter={[16,8]}>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'seotitle']} label="SEO Title">
                          <Input placeholder="Enter SEO title..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'slug']} label="SEO Slug">
                          <Input placeholder="seo-friendly-slug" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item {...restField} name={[name, 'seodescription']} label="SEO Description">
                          <TextArea rows={2} placeholder="Enter SEO description..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'seokeyword']} label="SEO Keywords">
                          <Select mode="tags" tokenSeparators={[',']} placeholder="Type keywords and press Enter..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'canonical']} label="Canonical URL">
                          <Input placeholder="https://example.com/canonical-url" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  {/* Open Graph Section */}
                  <Card size="small" style={{ marginBottom: 12 }} title={(<Space><ShareAltOutlined style={{ color: '#4267B2' }} /><span>Open Graph (Facebook & Social)</span></Space>)} bodyStyle={{ paddingBottom: 8 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Configure how your content appears when shared on Facebook, LinkedIn, and other social platforms
                    </Text>
                    <Row gutter={[16,8]}>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'og_title']} label="OG Title">
                          <Input placeholder="Open Graph title..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'og_url']} label="OG URL">
                          <Input placeholder="https://example.com/page-url" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item {...restField} name={[name, 'og_description']} label="OG Description">
                          <TextArea rows={2} placeholder="Open Graph description..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'og_image']} label="OG Image">
                          <Form.Item noStyle name={[name, 'og_image']}>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item
                            shouldUpdate={(prevValues, currentValues) => 
                              prevValues?.multilingual_seo?.[name]?.og_image !== currentValues?.multilingual_seo?.[name]?.og_image
                            }
                            noStyle
                          >
                            {({ getFieldValue }) => {
                              const ogImage = getFieldValue(['multilingual_seo', name, 'og_image']);
                              return (
                                <>
                                  {ogImage && (
                                    <div style={{ marginBottom: 12 }}>
                                      <div style={{
                                        padding: "8px 12px",
                                        background: "#fff7e6",
                                        border: "1px solid #ffd591",
                                        borderRadius: 6,
                                        color: "#fa8c16",
                                        marginBottom: 8,
                                      }}>
                                        <Text strong>{getImageUrl(ogImage)}</Text>
                                      </div>
                                      <div style={{
                                        width: "100%",
                                        height: "80px",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fafafa",
                                        marginBottom: 8,
                                      }}>
                                        <img
                                          src={getImageUrl(ogImage)}
                                          alt="OG preview"
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <ImgCrop aspect={1200/630} rotationSlider quality={1}>
                                    <Upload
                                      accept="image/*"
                                      showUploadList={false}
                                      beforeUpload={async (file) => {
                                        try {
                                          const formData = new FormData();
                                          formData.append("image_type", "images");
                                          formData.append("folder_name", "collections");
                                          formData.append("image", file);
                                          const response = await masterService.uploadImage(formData);
                                          const s3Key = response.data?.data?.s3_key;
                                          if (s3Key) {
                                            form.setFieldValue(['multilingual_seo', name, 'og_image'], s3Key);
                                            notify.success({ message: "Image uploaded successfully" });
                                          }
                                        } catch (error) {
                                          notify.error({ message: "Failed to upload image" });
                                        }
                                        return false;
                                      }}
                                    >
                                      <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                                        {ogImage ? "Change OG Image" : "Upload OG Image"}
                                      </Button>
                                    </Upload>
                                  </ImgCrop>
                                </>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'og_keywords']} label="OG Keywords">
                          <Select mode="tags" tokenSeparators={[',']} placeholder="Type OG keywords and press Enter..." />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  {/* Twitter Section */}
                  <Card size="small" style={{ marginBottom: 0 }} title={(<Space><TwitterOutlined style={{ color: '#1DA1F2' }} /><span>Twitter Cards</span></Space>)} bodyStyle={{ paddingBottom: 8 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Optimize how your content appears when shared on Twitter
                    </Text>
                    <Row gutter={[16,8]}>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'twitter_title']} label="Twitter Title">
                          <Input placeholder="Twitter card title..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'twitter_image']} label="Twitter Image">
                          <Form.Item noStyle name={[name, 'twitter_image']}>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item
                            shouldUpdate={(prevValues, currentValues) => 
                              prevValues?.multilingual_seo?.[name]?.twitter_image !== currentValues?.multilingual_seo?.[name]?.twitter_image
                            }
                            noStyle
                          >
                            {({ getFieldValue }) => {
                              const twitterImage = getFieldValue(['multilingual_seo', name, 'twitter_image']);
                              return (
                                <>
                                  {twitterImage && (
                                    <div style={{ marginBottom: 12 }}>
                                      <div style={{
                                        padding: "8px 12px",
                                        background: "#fff7e6",
                                        border: "1px solid #ffd591",
                                        borderRadius: 6,
                                        color: "#fa8c16",
                                        marginBottom: 8,
                                      }}>
                                        <Text strong>{getImageUrl(twitterImage)}</Text>
                                      </div>
                                      <div style={{
                                        width: "100%",
                                        height: "80px",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fafafa",
                                        marginBottom: 8,
                                      }}>
                                        <img
                                          src={getImageUrl(twitterImage)}
                                          alt="Twitter preview"
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <ImgCrop aspect={1200/600} rotationSlider quality={1}>
                                    <Upload
                                      accept="image/*"
                                      showUploadList={false}
                                      beforeUpload={async (file) => {
                                        try {
                                          const formData = new FormData();
                                          formData.append("image_type", "images");
                                          formData.append("folder_name", "collections");
                                          formData.append("image", file);
                                          const response = await masterService.uploadImage(formData);
                                          const s3Key = response.data?.data?.s3_key;
                                          if (s3Key) {
                                            form.setFieldValue(['multilingual_seo', name, 'twitter_image'], s3Key);
                                            notify.success({ message: "Image uploaded successfully" });
                                          }
                                        } catch (error) {
                                          notify.error({ message: "Failed to upload image" });
                                        }
                                        return false;
                                      }}
                                    >
                                      <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                                        {twitterImage ? "Change Twitter Image" : "Upload Twitter Image"}
                                      </Button>
                                    </Upload>
                                  </ImgCrop>
                                </>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item {...restField} name={[name, 'twitter_description']} label="Twitter Description">
                          <TextArea rows={2} placeholder="Twitter description..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'twitter_keywords']} label="Twitter Keywords">
                          <Select mode="tags" tokenSeparators={[',']} placeholder="Type Twitter keywords and press Enter..." />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  {/* Facebook Section */}
                  <Card size="small" style={{ marginBottom: 0 }} title={(<Space><FacebookOutlined style={{ color: '#4267B2' }} /><span>Facebook Cards</span></Space>)} bodyStyle={{ paddingBottom: 8 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                      Optimize how your content appears when shared on Facebook
                    </Text>
                    <Row gutter={[16,8]}>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'facebook_title']} label="Facebook Title">
                          <Input placeholder="Facebook card title..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'facebook_image']} label="Facebook Image">
                          <Form.Item noStyle name={[name, 'facebook_image']}>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item
                            shouldUpdate={(prevValues, currentValues) => 
                              prevValues?.multilingual_seo?.[name]?.facebook_image !== currentValues?.multilingual_seo?.[name]?.facebook_image
                            }
                            noStyle
                          >
                            {({ getFieldValue }) => {
                              const facebookImage = getFieldValue(['multilingual_seo', name, 'facebook_image']);
                              return (
                                <>
                                  {facebookImage && (
                                    <div style={{ marginBottom: 12 }}>
                                      <div style={{
                                        padding: "8px 12px",
                                        background: "#fff7e6",
                                        border: "1px solid #ffd591",
                                        borderRadius: 6,
                                        color: "#fa8c16",
                                        marginBottom: 8,
                                      }}>
                                        <Text strong>{getImageUrl(facebookImage)}</Text>
                                      </div>
                                      <div style={{
                                        width: "100%",
                                        height: "80px",
                                        border: "1px solid #d9d9d9",
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fafafa",
                                        marginBottom: 8,
                                      }}>
                                        <img
                                          src={getImageUrl(facebookImage)}
                                          alt="Facebook preview"
                                          style={{
                                            maxWidth: "100%",
                                            maxHeight: "100%",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                  <ImgCrop aspect={1200/630} rotationSlider quality={1}>
                                    <Upload
                                      accept="image/*"
                                      showUploadList={false}
                                      beforeUpload={async (file) => {
                                        try {
                                          const formData = new FormData();
                                          formData.append("image_type", "images");
                                          formData.append("folder_name", "collections");
                                          formData.append("image", file);
                                          const response = await masterService.uploadImage(formData);
                                          const s3Key = response.data?.data?.s3_key;
                                          if (s3Key) {
                                            form.setFieldValue(['multilingual_seo', name, 'facebook_image'], s3Key);
                                            notify.success({ message: "Image uploaded successfully" });
                                          }
                                        } catch (error) {
                                          notify.error({ message: "Failed to upload image" });
                                        }
                                        return false;
                                      }}
                                    >
                                      <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                                        {facebookImage ? "Change Facebook Image" : "Upload Facebook Image"}
                                      </Button>
                                    </Upload>
                                  </ImgCrop>
                                </>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item {...restField} name={[name, 'facebook_description']} label="Facebook Description">
                          <TextArea rows={2} placeholder="Facebook description..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Form.Item {...restField} name={[name, 'facebook_keywords']} label="Facebook Keywords">
                          <Select mode="tags" tokenSeparators={[',']} placeholder="Type Facebook keywords and press Enter..." />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Card>
              ))}
              <Button type="dashed" onClick={() => add()}>Add Language</Button>
            </>
          )}
        </Form.List>
      </Card>

      {/* Sticky Action Bar */}
      <div style={{
        position: 'sticky', 
        bottom: 0, 
        background: '#fff', 
        padding: '16px 0',
        borderTop: '1px solid #f0f0f0', 
        marginTop: 16, 
        zIndex: 1,
        borderRadius: '6px'
      }}>
        <Space size="middle">
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading} 
            icon={<SaveOutlined />}
            size="large"
          >
            Save SEO Settings
          </Button>
          {onBack && (
            <Button 
              onClick={onBack} 
              icon={<ArrowLeftOutlined />}
              size="large"
            >
              Back to Collection
            </Button>
          )}
        </Space>
      </div>
    </Form>
  );
};

export default CollectionSeoForm;
